import { useCallback, useState } from 'react';

/**
 * A PANE'S WIDTH, DRAGGED AND REMEMBERED (shell rules section 6, owner-33).
 *
 * Apple's contract for a split-view column is three numbers - a minimum, an ideal and a
 * maximum - and the chosen width persists: "Set reasonable defaults for minimum and
 * maximum pane sizes" (hig:
 * https://developer.apple.com/design/human-interface-guidelines/split-views) over
 * `navigationSplitViewColumnWidth(min:ideal:max:)` (doc:
 * https://developer.apple.com/documentation/swiftui/view/navigationsplitviewcolumnwidth(min:ideal:max:)).
 * `ui/SplitDivider` clamps and announces; these two helpers only say what the numbers ARE
 * and where the dragged one is kept.
 *
 * THE LIMITS ARE READ OFF THE TOKEN FILE rather than written here as numbers, which is
 * the same rule `ListDetailPane` follows: DESIGN.md's "tokens only" means a component may
 * not carry a second copy of a measure that can drift away from the one in `tokens.css`.
 */
export function tokenPx(name: string): number {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  const value = Number.parseFloat(raw);
  if (Number.isNaN(value)) return 0;
  return raw.endsWith('rem') ? value * 16 : value;
}

/** One pane's dragged width, kept in this browser. `null` means "never dragged". */
export function useRememberedWidth(key: string): readonly [number | null, (next: number) => void] {
  const [width, setWidth] = useState<number | null>(() => {
    try {
      const stored = window.localStorage.getItem(key);
      const parsed = stored === null ? Number.NaN : Number.parseInt(stored, 10);
      return Number.isFinite(parsed) ? parsed : null;
    } catch {
      return null;
    }
  });

  const remember = useCallback(
    (next: number) => {
      setWidth(next);
      try {
        window.localStorage.setItem(key, String(next));
      } catch {
        // The width still works for this session; there is nothing to tell the reader.
      }
    },
    [key],
  );

  return [width, remember];
}
